import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { ArrowDown, ArrowRight, ChevronDown, Menu, Star, X } from "lucide-react";
import SEO from "@/components/SEO";
import AIUsageGears from "@/components/AIUsageGears";
import CompanyLogoMarquee from "@/components/CompanyLogoMarquee";
import KitOptInForm from "@/components/KitOptInForm";
import ROIOfAIExplainer from "@/components/ROIOfAIExplainer";
import StateOfAIExplainer from "@/components/StateOfAIExplainer";
import { TEAM_PROFILES } from "@/data/teamProfiles";
import { AI_LEADERS_PROGRAM } from "@/data/aiLeadersProgram";
import { HEALTHCARE_PLAYBOOK_CONTENTS, HEALTHCARE_TEAM_PROGRESS, TEAM_PROGRAM_TESTIMONIALS } from "@/data/companyProgramProof";
import { library } from "@/data/aiWorkflows";
import { useTrackScrollDepth } from "@/hooks/use-posthog";
import { isReady, trackClick, trackEvent } from "@/utils/posthog";
import { useHydratedSearch } from "@/hooks/useHydratedSearch";
import { buildOrganizationJourneyUrl, HOME_REVISION } from "@/utils/organizationJourney";
import "./Home.css";

const navigation = [
  { href: "#how-we-help", label: "How we help" },
  { href: "#our-work", label: "Our work" },
  { href: "#our-team", label: "Who we are" },
  { href: "#resources", label: "Resources" },
];

function HomeLink({
  href,
  location,
  children,
  className = "home-text-link",
}: {
  href: string;
  location: string;
  children: ReactNode;
  className?: string;
}) {
  const search = useHydratedSearch();
  const destination = href.startsWith("/")
    ? buildOrganizationJourneyUrl(href, search, {
        source: "home",
        location,
        homeRevision: HOME_REVISION,
        audience: "organization",
      })
    : href;
  return (
    <a
      href={destination}
      className={className}
      onClick={() => {
        trackClick("Home - Explore", {
          location,
          destination,
          audience: "organization",
          home_revision: HOME_REVISION,
          source: "home",
        });
      }}
    >
      {children}
      <ArrowRight size={17} aria-hidden="true" />
    </a>
  );
}

function CallLink({
  location,
  children = "Discuss your team’s AI priorities",
  className = "home-button",
}: {
  location: string;
  children?: ReactNode;
  className?: string;
}) {
  const search = useHydratedSearch();
  return (
    <a
      href={buildOrganizationJourneyUrl("/ai-call", search, {
        source: "home", location, audience: "organization", homeRevision: HOME_REVISION,
      })}
      className={className}
      onClick={() =>
        trackClick(
          location === "hero"
            ? "Hero - Book Strategy Session"
            : "Home - Book Strategy Session",
          {
            location,
            destination: "/ai-call",
            goal: "strategy_session",
            audience: "organization",
            home_revision: HOME_REVISION,
            source: "home",
          },
        )
      }
    >
      {children}
      <ArrowRight size={17} aria-hidden="true" />
    </a>
  );
}

function HomeHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  return (
    <header
      className="home-header"
      onKeyDown={(event) => {
        if (event.key === "Escape" && menuOpen) {
          setMenuOpen(false);
          menuButton.current?.focus();
        }
      }}
    >
      <div className="home-container home-header-inner">
        <a href="/" aria-label="Tech Leaders home">
          <img
            src="/orange-logo.png"
            alt="Tech Leaders"
            width="234"
            height="40"
            className="home-logo"
          />
        </a>
        <nav className="home-desktop-nav" aria-label="Main navigation">
          {navigation.map((item) => (
            <a key={item.href} href={item.href}>
              {item.label}
            </a>
          ))}
        </nav>
        <div className="home-header-actions">
          <CallLink
            location="navigation"
            className="home-button home-button-small"
          >
            Book a call
          </CallLink>
          <button
            type="button"
            ref={menuButton}
            className="home-menu-button"
            aria-expanded={menuOpen}
            aria-controls="home-mobile-navigation"
            aria-label={menuOpen ? "Close navigation" : "Open navigation"}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </button>
        </div>
      </div>
      <nav
        id="home-mobile-navigation"
        className="home-mobile-nav home-container"
        aria-label="Mobile navigation"
        hidden={!menuOpen}
      >
        {navigation.map((item) => (
          <a
            key={item.href}
            href={item.href}
            onClick={() => setMenuOpen(false)}
          >
            {item.label}
            <ArrowDown size={16} aria-hidden="true" />
          </a>
        ))}
      </nav>
    </header>
  );
}

// The 10-80-10 split as taught in public/s2p-elevate-ai-guide.html (Exercise 3.1).
const workShare = [
  {
    share: "10%",
    part: "people",
    title: "People set the direction",
    description: "Choose the task, gather the inputs, and define what good looks like.",
  },
  {
    share: "80%",
    part: "ai",
    title: "AI does the heavy lifting",
    description: "Drafting, research, and the repeatable steps in between.",
  },
  {
    share: "10%",
    part: "people",
    title: "People review and own it",
    description: "Check the result, handle the edge cases, and own the outcome.",
  },
];

// Jobs to be Done as taught in public/s2p-elevate-ai-guide.html (Exercise 2.1);
// the examples are from that guide's list of Jobs.
const aiReturns = [
  {
    tone: "gain",
    title: "Save time, make money",
    description: "Speed up recurring work that brings in revenue, like prepping prospect outreach.",
  },
  {
    tone: "gain",
    title: "Save time, save money",
    description: "Take hours out of recurring work, like drafting the weekly board update.",
  },
  {
    tone: "waste",
    title: "Waste",
    description: "Tool-first use with no clear job burns time and tokens, and can leak sensitive data.",
  },
];

const services = [
  {
    number: "01",
    title: "Give your team a shared foundation.",
    usefulWhen: "Your people are experimenting with AI, but adoption is uneven.",
    description:
      "Bring leaders and staff together around what AI can do, where it fits, and how to use it responsibly. We facilitate decisions and teach practical skills through your team’s real work.",
    deliverables: [
      "Leadership alignment and facilitated workshops",
      "Hands-on AI training for workplace teams",
      "Practical playbooks, examples, and templates",
    ],
  },
  {
    number: "02",
    title: "Turn useful ideas into working workflows.",
    usefulWhen: "You have promising use cases and need help implementing them.",
    description:
      "Choose the work worth improving, then map, build, and tune AI workflows and agents alongside your team. Connect the instructions, context, and quality checks to the result you need.",
    deliverables: [
      "Workflow discovery and prioritization",
      "Custom AI workflows and agent co-building",
      "Human review and clear operating boundaries",
    ],
  },
  {
    number: "03",
    title: "Help the new way of working stick.",
    usefulWhen: "You’ve completed training and want the new habits to stick.",
    description:
      "Keep applying what you learn with coaching, implementation support, and a place to work through obstacles. Help people build confidence, review results, and make the next improvement.",
    deliverables: [
      "Implementation coaching and ongoing support",
      "AI Mastermind sessions and peer feedback",
      "Workflow review and continued improvement",
    ],
  },
];

const libraryPreview = library.find(
  (workflow) => workflow.slug === "meeting-decisions-action-items",
);

const process = [
  {
    title: "Understand the work",
    description:
      "Start with your priorities, the people involved, and what is already happening with AI.",
  },
  {
    title: "Choose a useful starting point",
    description:
      "Agree on the scope, a practical use case, and what a good result should look like.",
  },
  {
    title: "Learn and build together",
    description:
      "Apply the training to real workflows. Practice, build, and review with your team involved.",
  },
  {
    title: "Support the next improvement",
    description:
      "Use feedback, coaching, and agreed measures to improve the work and continue adoption.",
  },
];

const questions = [
  {
    question: "Can you help if our teams are at different stages with AI?",
    answer:
      "Yes. We work with leaders and teams who are getting started, as well as people already using AI workflows or agents. We start with your organization’s current experience and priorities, then shape the training and implementation work around them.",
  },
  {
    question: "Do participants need a technical background?",
    answer:
      "No technical background is required for our AI training and agent co-building programs. We connect the learning to participants’ roles and recurring work. The tools, integrations, and technical requirements for an engagement are discussed when we agree on scope.",
  },
  {
    question: "How do you approach responsible use and sensitive information?",
    answer:
      "Our training covers responsible AI use, output validation, and human accountability. Before applying AI to a workflow, we discuss your approved tools, data boundaries, access, and review requirements. Your organization’s policies and decision rights guide the work.",
  },
  {
    question: "Can you work with our organization privately?",
    answer:
      "Yes. We offer private, customized programs for organizations, with on-site or virtual delivery. We discuss the participants, business priorities, delivery format, and support needs before defining the engagement.",
  },
  {
    question: "How much time does an engagement take?",
    answer: `AI 30 is our ${AI_LEADERS_PROGRAM.durationDays}-day program for workplace teams, including smaller cohorts. It includes ${AI_LEADERS_PROGRAM.liveHours} hours of live training and ${AI_LEADERS_PROGRAM.supportDays} days of implementation support. We agree on seats, priorities, delivery format, and schedule in your team proposal. Private workshops and implementation support can be scoped around your organization’s needs.`,
  },
];

const homeSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Tech Leaders",
  url: "https://www.technical-leaders.com/",
  logo: "https://www.technical-leaders.com/orange-logo.png",
  description:
    "Practical AI training, workflow implementation, and adoption support for leaders and organizations.",
  email: "todd@technical-leaders.com",
  founder: [
    { "@type": "Person", name: "Todd Larsen" },
    { "@type": "Person", name: "Stephen Bates" },
  ],
  sameAs: ["https://www.linkedin.com/company/technical-leaders"],
};

// Short, verbatim excerpts from the existing public testimonial.to collection.
// See docs/plans/2026-10-19-homepage-company-overview.md for provenance.
const testimonials = [
  ...TEAM_PROGRAM_TESTIMONIALS,
  {
    name: "Brett P Wilzbach",
    role: "Program participant",
    quote:
      "Todd shows where the rubber hits the road because he's actively doing it for his companies at the exact same time.",
  },
];

function Testimonials() {
  return (
    <section
      className="home-section home-feedback"
      aria-labelledby="feedback-title"
    >
      <div className="home-container">
        <p className="home-eyebrow">Participant perspectives</p>
        <h2 id="feedback-title">What it’s like to learn and work with us.</h2>
        <p className="home-section-intro">
          Feedback from leaders and professionals who have taken part in our
          programs.
        </p>
        <div className="home-quotes">
          {testimonials.map((item) => (
            <figure key={item.name}>
              <blockquote>
                <p>“{item.quote}”</p>
              </blockquote>
              <figcaption>
                <cite>{item.name}</cite>
                <span>{item.role}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

const Index = () => {
  const search = useHydratedSearch();
  const { search: routeSearch } = useLocation();
  useTrackScrollDepth("Home");
  useEffect(() => {
    if (search !== routeSearch) return;
    const trackView = () => {
      if (!isReady()) return false;
      trackEvent("Homepage Viewed", {
        audience: "organization",
        home_revision: HOME_REVISION,
      });
      return true;
    };
    if (trackView()) return;
    let attempts = 0;
    const retry = window.setInterval(() => {
      attempts += 1;
      if (trackView() || attempts >= 100) window.clearInterval(retry);
    }, 100);
    return () => window.clearInterval(retry);
  }, [search, routeSearch]);

  return (
    <div className="home-page">
      <SEO
        title="AI Training & Implementation for Organizations | Tech Leaders"
        siteName="Tech Leaders"
        author="Tech Leaders"
        description="We help leaders and organizations put AI to work through practical training, custom workflows and agents, and ongoing adoption support. Meet our team and explore our work."
        keywords={[
          "organizational AI adoption",
          "AI training for teams",
          "AI implementation",
          "leadership development",
          "custom AI workflows",
        ]}
        structuredData={homeSchema}
      />
      <a className="home-skip-link" href="#main-content">
        Skip to content
      </a>
      <HomeHeader />
      <main id="main-content" tabIndex={-1}>
        <section className="home-hero-section" aria-labelledby="home-title">
          <div className="home-hero home-container">
            <div className="home-hero-copy">
              <p className="home-eyebrow">
                Practical AI adoption for organizations
              </p>
              <h1 id="home-title">
                Help your organization put AI to work.
              </h1>
              <p className="home-hero-intro">
                Practical training, hands-on workflow building, and ongoing
                support. We help your team use AI in everyday work, whether
                you’re getting started or improving the agents you already use.
              </p>
              <div className="home-actions">
                <CallLink location="hero" />
                <a href="#how-we-help" className="home-text-link">
                  Explore how we help <ArrowDown size={17} aria-hidden="true" />
                </a>
              </div>
              <p className="home-call-note">
                A 15-minute phone call with Todd. Bring your team’s priorities;
                we’ll discuss a useful next step.
              </p>
            </div>
            <div className="home-methodology">
              <AIUsageGears />
              <p className="home-methodology-note">
                We help your team choose the right gear for the work, then build
                the skills and workflows to use it well.
              </p>
              <a href="#our-approach" className="home-text-link">
                How we put this into practice{" "}
                <ArrowDown size={17} aria-hidden="true" />
              </a>
            </div>
          </div>
        </section>

        <CompanyLogoMarquee
          className="home-organizations"
          heading="We’ve trained leaders and staff from organizations including"
          footnote="8,000+ professionals trained across our programs."
        />

        <aside className="home-container home-early-library" aria-labelledby="home-early-library-title">
          <div>
            <p className="home-eyebrow">Try the approach on your own work</p>
            <h2 id="home-early-library-title">Start with one useful workflow.</h2>
            <p>Get instructions for meetings, research, reporting, and follow-ups. Choose a task your team repeats, adapt the example, and check the result.</p>
          </div>
          <div className="home-early-library-actions">
            <HomeLink href="/library" location="early_library">Get the free workflow library</HomeLink>
            <HomeLink href="/library" location="early_library_inbox">Send it to my inbox</HomeLink>
          </div>
        </aside>

        <section
          className="home-section home-sketch"
          aria-labelledby="state-of-ai-title"
        >
          <div className="home-container home-sketch-grid">
            <div className="home-sketch-copy">
              <p className="home-eyebrow">The state of AI</p>
              <h2 id="state-of-ai-title">
                Loud at the edges.
                <br />
                Useful in the middle.
              </h2>
              <p>
                Most of the AI conversation pulls toward an extreme: hype or
                letdown, doom or utopia. The useful work happens in the middle,
                with a simple rule for sharing the work between your people
                and AI.
              </p>
            </div>
            <StateOfAIExplainer />
            <div className="home-sketch-detail">
              <h3>The 10-80-10 rule</h3>
              <ol>
                {workShare.map((step) => (
                  <li key={step.title}>
                    <span className="home-sketch-marker" data-tone={step.part}>
                      {step.share}
                    </span>
                    <div>
                      <strong>{step.title}</strong>
                      <p>{step.description}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section
          id="how-we-help"
          className="home-section home-container"
          aria-labelledby="help-title"
        >
          <div className="home-section-heading">
            <div>
              <p className="home-eyebrow">How we help</p>
              <h2 id="help-title">
                Clarity for your leaders.
                <br />
                Capability for your team.
              </h2>
            </div>
            <p>
              Successful AI adoption involves people, processes, and technology.
              We help you connect all three, around your organization’s
              priorities.
            </p>
          </div>
          <div className="home-services">
            {services.map((service) => (
              <article key={service.number}>
                <span className="home-number" aria-hidden="true">
                  {service.number}
                </span>
                <h3>{service.title}</h3>
                <p className="home-service-fit">
                  <strong>Useful when:</strong> {service.usefulWhen}
                </p>
                <p>{service.description}</p>
                <ul>
                  {service.deliverables.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section
          className="home-section home-sketch"
          aria-labelledby="roi-of-ai-title"
        >
          <div className="home-container home-sketch-grid home-sketch-grid-reverse">
            <div className="home-sketch-copy">
              <p className="home-eyebrow">The ROI of AI</p>
              <h2 id="roi-of-ai-title">
                Aim AI at a job.
                <br />
                Skip the waste.
              </h2>
              <p>
                AI pays off when it saves time on a specific, recurring job, and
                that time helps your team make money or save it. So instead of
                asking “How can we use this tool?”, ask “Which job should this
                improve?”
              </p>
            </div>
            <ROIOfAIExplainer />
            <div className="home-sketch-detail">
              <h3>Find the job to be done</h3>
              <ul>
                {aiReturns.map((item) => (
                  <li key={item.title}>
                    <span className="home-sketch-marker" data-tone={item.tone}>
                      {item.tone === "gain" ? (
                        <Star aria-hidden="true" />
                      ) : (
                        <X aria-hidden="true" />
                      )}
                    </span>
                    <div>
                      <strong>{item.title}</strong>
                      <p>{item.description}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section
          id="our-work"
          className="home-section home-case"
          aria-labelledby="work-title"
        >
          <div className="home-container home-case-grid">
            <div>
              <p className="home-eyebrow">Our work / Health tech</p>
              <h2 id="work-title">
                30 leaders.
                <br />
                One shared language for AI.
              </h2>
              <p className="home-case-intro">
                A $75M health tech company needed a common foundation for AI
                adoption across executives, managers, and analysts.
              </p>
              <p>
                We delivered a private, customized program connecting AI
                fundamentals to department workflows, a company playbook, and
                ongoing implementation support.
              </p>
              <div className="home-actions">
                <CallLink location="case_study" />
                <HomeLink
                  href="/healthcare-ai-case-study"
                  location="case_study"
                  className="home-text-link home-text-link-light"
                >
                  Read the client story
                </HomeLink>
              </div>
            </div>
            <div className="home-case-details">
              <dl>
                <div>
                  <dt>Participants</dt>
                  <dd>30 leaders</dd>
                </div>
                <div>
                  <dt>Program timeline</dt>
                  <dd>3 weeks</dd>
                </div>
                <div>
                  <dt>Format</dt>
                  <dd>Private, custom program</dd>
                </div>
              </dl>
              <figure className="home-case-playbook">
                <figcaption>
                  <h3>Inside the company AI-First Playbook</h3>
                  <p>A deliverable from this engagement</p>
                </figcaption>
                <ul>
                  {HEALTHCARE_PLAYBOOK_CONTENTS.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <HomeLink
                  href="/healthcare-ai-case-study#approach"
                  location="case_playbook"
                  className="home-text-link home-text-link-light"
                >
                  Explore what we delivered
                </HomeLink>
              </figure>
            </div>
          </div>
          <div className="home-container home-case-progress">
            <h3>What changed for the team</h3>
            <table>
              <caption className="sr-only">The client’s starting point and what the program helped them build</caption>
              <thead>
                <tr><th scope="col">Where they started</th><th scope="col">What they built</th></tr>
              </thead>
              <tbody>
                {HEALTHCARE_TEAM_PROGRESS.map((item) => (
                  <tr key={item.before}><td>{item.before}</td><td>{item.after}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <article
            className="home-container home-case-platform"
            aria-labelledby="longhand-case-title"
          >
            <div>
              <p className="home-eyebrow">Our platform / Longhand</p>
              <h3 id="longhand-case-title">
                From training to operational AI.
              </h3>
              <p>
                Longhand is our platform for building and running AI
                workflows. It connects practical AI skills with the
                repeatable work your team does every day.
              </p>
            </div>
            <div>
              <p>
                The related enterprise classification case study shows the
                approach in action. In the workflow demo, agents research
                each item and propose a classification. Analysts review
                uncertain results, with their corrections captured as
                feedback for future runs.
              </p>
              <HomeLink
                href="https://www.getlonghand.com/#customer-story"
                location="longhand_case_study"
                className="home-text-link home-text-link-light"
              >
                See the operational case study on Longhand
              </HomeLink>
            </div>
          </article>
          <aside className="home-container home-library-invitation" aria-label="Try the approach">
            <div>
              <h3>Want to try a workflow with your team?</h3>
              <p>Get the free library. Pick a task, adapt the instructions, and review the result.</p>
            </div>
            <HomeLink href="/library" location="case_library" className="home-text-link home-text-link-light">
              Get the free workflow library
            </HomeLink>
          </aside>
        </section>

        <aside className="home-credential" aria-label="OpenAI partnership">
          <div className="home-container home-credential-inner">
            <img
              src="/openai-wordmark.svg"
              alt="OpenAI"
              width="112"
              height="32"
            />
            <p>
              <strong>Tech Leaders is an OpenAI SMB Channel Partner.</strong>
              <span>
                {" "}
                Training, workflow design, and adoption support for your
                business.
              </span>
            </p>
            <HomeLink href="/openai-partner" location="partnership">
              About the partnership
            </HomeLink>
          </div>
        </aside>

        <section
          className="home-section home-container"
          aria-labelledby="engagement-title"
        >
          <div className="home-section-heading">
            <div>
              <p className="home-eyebrow">Ways to work with us</p>
              <h2 id="engagement-title">
                The right support
                <br />
                for your next step.
              </h2>
            </div>
            <p>
              Choose a focused program or talk with us about a private
              engagement. We’ll discuss your team, priorities, and timing, then
              confirm what the work includes.
            </p>
          </div>
          <div className="home-engagements">
            <article>
              <p className="home-eyebrow">AI 30 · For workplace teams</p>
              <h3>Put AI to work with your team</h3>
              <p>
                Build useful AI workflows, measure results, and lead adoption
                together in 30 days.
              </p>
              <p className="home-engagement-facts">
                {AI_LEADERS_PROGRAM.liveHours} live training hours ·{" "}
                {AI_LEADERS_PROGRAM.supportDays} days of support
                <br />
                Seats for your team · Smaller cohorts welcome
              </p>
              <HomeLink href="/ai-30" location="team_program">
                Explore the 30-day team program
              </HomeLink>
              <HomeLink
                href="/ai-30/summary"
                location="team_program_summary"
                className="home-text-link home-engagement-summary"
              >
                Share the program overview with your team
              </HomeLink>
            </article>
            <article>
              <p className="home-eyebrow">For your organization or event</p>
              <h3>Private workshops and implementation</h3>
              <p>
                Bring us into your organization for facilitated workshops,
                practical AI training, workflow implementation, or coaching
                shaped around your priorities.
              </p>
              <p className="home-engagement-facts">
                Scope and delivery agreed with your team
                <br />
                On-site or virtual options
              </p>
              <CallLink location="private_engagement" className="home-text-link" />
            </article>
          </div>
        </section>

        <section
          id="our-team"
          className="home-section home-container"
          aria-labelledby="team-title"
        >
          <div className="home-section-heading">
            <div>
              <p className="home-eyebrow">Who we are</p>
              <h2 id="team-title">
                Practitioners, educators,
                <br />
                and people in your corner.
              </h2>
            </div>
            <div>
              <p>
                Tech Leaders was founded by Todd Larsen and Stephen Bates to
                help technical professionals grow as leaders. Our work brings
                that foundation in leadership development to the challenges of
                AI adoption.
              </p>
              <p>
                Our founders and educator network bring experience in
                engineering, executive coaching, product, and organizational
                change. We facilitate, train, and coach alongside your team.
              </p>
            </div>
          </div>
          <div className="home-team-grid">
            {TEAM_PROFILES.map((person) => (
              <article key={person.name} className="home-person">
                <img
                  src={person.image}
                  alt={person.name}
                  width="112"
                  height="112"
                  loading="lazy"
                />
                <div>
                  <h3>{person.name}</h3>
                  <p className="home-person-role">{person.role}</p>
                </div>
                <p className="home-person-bio">{person.description}</p>
              </article>
            ))}
          </div>
          <HomeLink href="/forward-deployed-educators" location="team">
            Read the full team profiles
          </HomeLink>
        </section>

        <section
          id="our-approach"
          className="home-section home-approach"
          aria-labelledby="approach-title"
        >
          <div className="home-container">
            <div className="home-section-heading">
              <div>
                <p className="home-eyebrow">Our approach</p>
                <h2 id="approach-title">
                  Start with the work.
                  <br />
                  Build with your people.
                </h2>
              </div>
              <p>
                Whether your team is exploring AI or already building agents, we
                begin with the work you want to improve and what a useful result
                would look like.
              </p>
            </div>
            <ol className="home-process">
              {process.map((step, index) => (
                <li key={step.title}>
                  <span className="home-number" aria-hidden="true">
                    0{index + 1}
                  </span>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <Testimonials />

        <section
          className="home-section home-container home-faq"
          aria-labelledby="questions-title"
        >
          <div>
            <p className="home-eyebrow">Practical questions</p>
            <h2 id="questions-title">
              A few things
              <br />
              you may be wondering.
            </h2>
            <p>
              Have a question specific to your organization?{" "}
              <a href="mailto:todd@technical-leaders.com">Email Todd</a> or book
              a conversation.
            </p>
          </div>
          <div>
            {questions.map((item) => (
              <details key={item.question}>
                <summary>
                  {item.question}
                  <ChevronDown size={19} aria-hidden="true" />
                </summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section
          id="resources"
          className="home-section home-resources"
          aria-labelledby="resources-title"
        >
          <div className="home-container">
            <div className="home-section-heading">
              <div>
                <p className="home-eyebrow">Explore our thinking</p>
                <h2 id="resources-title">
                  Get a feel for
                  <br />
                  how we work.
                </h2>
              </div>
              <p>
                Start with practical examples, or explore how we can help at
                your next event.
              </p>
            </div>
            <div className="home-resource-links">
              <HomeLink
                href="/library"
                location="resource_workflows"
                className="home-resource-feature"
              >
                <span className="home-resource-media home-resource-media-library">
                  <img
                    src="/screenshots/home/prompt-library-preview.webp"
                    alt="Preview of the prompt library with workflow search, team filters, and tasks for meeting notes and account research"
                    width={1280}
                    height={720}
                    loading="lazy"
                    decoding="async"
                  />
                </span>
                <span className="home-resource-copy">
                  <span className="home-eyebrow">A workflow for the work ahead</span>
                  <strong>Put AI to work on something you already do.</strong>
                  <span className="home-resource-description">
                    {library.length} practical AI workflows. Find a task, add your notes or documents, and make a draft you can check and use.
                  </span>
                </span>
                <span className="home-resource-action">Get the free workflow library</span>
              </HomeLink>
              <HomeLink href="/speak" location="resource_speaking" className="home-resource-feature">
                <span className="home-resource-media home-resource-media-speaking">
                  <img
                    src="/todd-speaking-portrait.webp"
                    alt="Todd Larsen speaking on stage with a microphone and presentation remote"
                    width={1000}
                    height={1250}
                    loading="lazy"
                    decoding="async"
                  />
                </span>
                <span className="home-resource-copy">
                  <span className="home-eyebrow">For your next event</span>
                  <strong>Bring practical AI to your next event.</strong>
                  <span className="home-resource-description">
                    Talks and hands-on workshops with Todd Larsen on AI adoption, agents, and leadership.
                  </span>
                </span>
                <span className="home-resource-action">See Todd in action</span>
              </HomeLink>
            </div>
            <div id="home-library-signup" className="home-library">
              <div>
                <p className="home-eyebrow">
                  A practical resource for your team
                </p>
                <h3>Get the Fortune 100 AI Prompt Library.</h3>
                <p>
                  {library.length} practical AI workflows for leaders and teams.
                  Find a task, adapt the instructions, and put the ideas to
                  work.
                </p>
                {libraryPreview && (
                  <div className="home-library-preview">
                    <p className="home-eyebrow">An example from the library</p>
                    <h4>{libraryPreview.title}</h4>
                    <p>
                      <strong>You bring:</strong> {libraryPreview.requiredInputs[0]}.
                    </p>
                    <p>
                      <strong>You get:</strong> A draft of the decisions and
                      actions, with owners, deadlines, and source references.
                      Missing details stay marked for confirmation.
                    </p>
                    <HomeLink
                      href="/library"
                      location="library_example"
                    >
                      Get access to this workflow
                    </HomeLink>
                  </div>
                )}
                <p className="home-small">
                  Sent to your inbox. Unsubscribe at any time.
                </p>
              </div>
              <div className="home-library-form">
                <KitOptInForm
                  uid="fb2b5743bc"
                  conversionName="Fortune 100 Library Signup"
                  location="home_resources"
                  fallbackHref={buildOrganizationJourneyUrl("/library", search, {
                    source: "home", location: "home_resources", audience: "organization", homeRevision: HOME_REVISION,
                  })}
                  fallbackLabel="Send me the library"
                  confirmationPath="/library-confirmed"
                  submitLabel="Send me the library"
                  firstNameOptional
                  showFieldLabels
                  journeyContext={{ audience: "organization", home_revision: HOME_REVISION }}
                />
                <noscript>
                  <a href={buildOrganizationJourneyUrl("/library", "", { source: "home", location: "library_noscript", audience: "organization", homeRevision: HOME_REVISION })} className="home-text-link">
                    Open the library signup page
                  </a>
                </noscript>
              </div>
            </div>
          </div>
        </section>

        <section
          className="home-section home-contact"
          aria-labelledby="contact-title"
        >
          <div className="home-container home-contact-inner">
            <div>
              <p className="home-eyebrow">Let’s talk about your organization</p>
              <h2 id="contact-title">
                What would useful AI
                <br />
                look like for your team?
              </h2>
              <p>
                In a 15-minute phone call with Todd, we’ll discuss your
                priorities, how your people use AI today, and where training or
                implementation support could help.
              </p>
            </div>
            <div>
              <CallLink location="closing" />
              <a
                href="mailto:todd@technical-leaders.com"
                className="home-contact-email"
              >
                todd@technical-leaders.com
              </a>
            </div>
          </div>
        </section>
      </main>
      <footer className="home-footer">
        <div className="home-container home-footer-inner">
          <div>
            <a href="/" aria-label="Tech Leaders home">
              <img
                src="/orange-logo.png"
                alt="Tech Leaders"
                width="234"
                height="40"
                className="home-logo"
              />
            </a>
            <p>
              Practical AI. Capable people.
              <br />© 2026 Tech Leaders.
            </p>
          </div>
          <nav aria-label="Footer navigation">
            <a href="#how-we-help">How we help</a>
            <a href="/forward-deployed-educators">Our people</a>
            <a href="/healthcare-ai-case-study">Client story</a>
            <a href="/openai-partner">OpenAI partnership</a>
            <a href="/privacy-policy">Privacy policy</a>
            <a href="https://billing.stripe.com/p/login/28oaFm1om8Za4XC000">
              My membership
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default Index;
